import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {Button, Input, Popover} from 'components/antd';
import {
  usePoolQuery,
  ProspectPool,
  ProspectTag,
  useAddTagToProspectPoolMutation,
  useRemoveTagFromProspectPoolMutation,
  useCreateProspectTagMutation,
  useUpdateProspectTagPositionMutation,
} from 'api';
import {Handle, Cross, MoreVertical} from 'components/Ui/Icon';
import TagEdit from './TagEdit';
import {getTagFontColor, tagColors} from 'helpers/tagColors';
import useClientUser from 'hooks/useClientUser';
import useProspectContext from '../../useProspectContext';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';

const kanaToHira = (str: string) =>
  str.replace(/[\u30a1-\u30f6]/g, (match) => {
    const chr = match.charCodeAt(0) - 0x60;
    return String.fromCharCode(chr);
  });

interface Props {
  prospectPool: ProspectPool;
}

export default ({prospectPool}: Props) => {
  const {isAdmin} = useClientUser();
  const {poolId} = useProspectContext();
  const prospectPoolUuId = prospectPool?.uuid;
  const [tagName, setTagName] = useState('');
  const [isPopoverVisible, setPopoverVisible] = useState(null);

  const {data: {pool: {prospectTags = []} = {}} = {}} = usePoolQuery({
    variables: {uuid: poolId},
    skip: !poolId,
    fetchPolicy: 'cache-and-network',
  });

  const getRandomColor = (): string => {
    const colors = tagColors.map((color) => color.hex);
    const color = colors[Math.floor(Math.random() * colors.length)];
    return color;
  };

  const randomColor = React.useMemo(() => getRandomColor(), [prospectTags]);

  const [addTagToProspectPool] = useAddTagToProspectPoolMutation();
  const [removeTagFromProspectPool] = useRemoveTagFromProspectPoolMutation();

  const [createProspectTag] = useCreateProspectTagMutation({
    onCompleted: ({createProspectTag: {prospectTag = {}} = {}}) => {
      setTagName('');
      onAdd(prospectTag);
    },
  });

  const filteredTags = useMemo(
    () =>
      prospectTags?.filter((tag) =>
        kanaToHira(tag.name).includes(kanaToHira(tagName)),
      ),
    [prospectTags, tagName, prospectPool?.prospectTags],
  );

  const alreadyNameExist = useMemo(
    () => prospectTags?.some((tag) => tag.name === tagName),
    [prospectTags, tagName],
  );

  const alreadyAdded = React.useCallback(
    (tag) =>
      prospectPool?.prospectTags.some(
        (prospectTag) => prospectTag.id === tag.id,
      ),
    [prospectTags, prospectPool?.prospectTags],
  );

  const onChange = React.useCallback(
    (e: any) => setTagName(e.target.value),
    [],
  );

  const onAdd = (tag: ProspectTag) => {
    addTagToProspectPool({
      variables: {
        uuid: prospectPoolUuId,
        tagId: tag.id,
      },
    });
  };

  const onRemove = (tag: ProspectTag) => {
    removeTagFromProspectPool({
      variables: {
        uuid: prospectPoolUuId,
        tagId: tag.id,
      },
    });
  };

  const onCreate = () => {
    if (!!tagName && !alreadyNameExist) {
      createProspectTag({
        variables: {
          attributes: {
            name: tagName,
            color: randomColor,
            poolId: poolId,
          },
        },
      });
    }
  };

  const [updateProspectTagPosition] =
    useUpdateProspectTagPositionMutation();

  const onDragEnd = React.useCallback(
    (result: DropResult) => {
      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      const items = [...filteredTags];
      const [removed] = items.splice(sourceIndex, 1);
      items.splice(destIndex, 0, removed);

      updateProspectTagPosition({
        variables: {
          id: removed.id,
          position: destIndex,
        },
      });
    },
    [filteredTags],
  );

  return (
    <Container>
      <PaddingWrapper>
        <InputBox>
          {prospectPool?.prospectTags?.map((tag) => (
            <CreatedTag key={tag.id} color={tag.color}>
              <span>{tag.name}</span>
              <CrossWrapper onClick={() => onRemove(tag)}>
                <Cross />
              </CrossWrapper>
            </CreatedTag>
          ))}

          <InputWrapper>
            <TagInput
              type="search"
              name="search"
              bordered={false}
              placeholder={tagName}
              defaultValue={''}
              value={tagName}
              onChange={onChange}
              onPressEnter={isAdmin && onCreate}
            />
          </InputWrapper>
        </InputBox>

        <h3>タグを編集</h3>
      </PaddingWrapper>

      <ScrollWrapper>
        <List>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {filteredTags?.map((tag, index) => (
                    <Draggable key={tag.id} draggableId={tag.id} index={index}>
                      {(provided, snapshot) => (
                        <DraggableItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={snapshot.isDragging ? 'dragging' : ''}>
                          <HandleWrapper {...provided.dragHandleProps}>
                            <Handle />
                          </HandleWrapper>
                          <Tag
                            key={tag.id}
                            onClick={() => {
                              onAdd(tag);
                              setTagName('');
                            }}
                            added={alreadyAdded(tag)}>
                            <div>
                              <TagRectangle color={tag.color} />
                              <TagName>{tag.name}</TagName>
                              {isAdmin && (
                                <Popover
                                  content={() => (
                                    <TagEdit
                                      tag={tag}
                                      onClose={() => setPopoverVisible(null)}
                                    />
                                  )}
                                  trigger="click"
                                  visible={isPopoverVisible === tag.id}
                                  onVisibleChange={(visible) =>
                                    setPopoverVisible(visible ? tag.id : null)
                                  }
                                  placement="bottomRight">
                                  <Button
                                    type="text"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    // className='ml-auto'
                                  >
                                    <MoreVertical />
                                  </Button>
                                </Popover>
                              )}
                            </div>
                          </Tag>
                        </DraggableItem>
                      )}
                    </Draggable>
                  ))}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </List>
      </ScrollWrapper>
      {tagName && !alreadyNameExist && isAdmin && (
        <NewTag>
          <h3>新規作成</h3>
          <Tag onClick={onCreate}>
            <div>
              <TagRectangle color={randomColor} />
              <TagName>{tagName}</TagName>
            </div>
          </Tag>
        </NewTag>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 500px;

  h3 {
    margin-top: 10px;
    font-size: 10px;
    color: var(--text-color-2);
  }
`;

const PaddingWrapper = styled.div`
  padding: 13px 11px 0 11px;
`;

const InputBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: 11px;
  width: 100%;
  min-height: 34px;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #e1e6eb;
  box-sizing: border-box;
  border-radius: 2px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  border: none;
  padding: 0px;
  width: auto;
  background: transparent;
  font-size: inherit;
  line-height: 20px;
  flex: 1 1 60px;
  min-width: 60px;
  margin: 0px 6px 6px 0px;
`;

const TagInput = styled(Input)`
  border: none;
  padding: 0px;
  .ant-input {
    background: #ffffff;
    &::placeholder {
      font-size: 10px;
      color: var(--text-color-3);
    }
  }

  &:focus-within {
    background-color: white;
  }
  .ant-input:focus-within {
    color: black;
    background: white;
  }
`;

const CreatedTag = styled.span<{color?: string}>`
  margin-right: 6px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  padding: 2px 8px;
  background: ${({color}) => (color ? `${color}` : 'black')};
  border-radius: 2px;
  span {
    font-size: 12px;
    font-weight: bold;
    line-height: 13px;
    color: ${({color}) => getTagFontColor(color)};
  }
  svg {
    margin-left: 6px;
    width: 7px;
    height: 7px;
    path {
      stroke-width: 2;
      stroke: ${({color}) => getTagFontColor(color)};
    }
  }
`;

const CrossWrapper = styled.span`
  cursor: pointer;
`;

const ScrollWrapper = styled.div`
  max-height: 200px;
  overflow: scroll;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    // margin-bottom: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NewTag = styled.div`
  border-top: solid 1px #e1e6eb;

  h3 {
    margin-bottom: 0px;
    padding: 0px 11px;
  }
`;

const TagRectangle = styled.div<{color?: string}>`
  width: 14px;
  height: 14px;
  border-radius: 2px;
  background: ${({color}) => (color ? `${color}` : 'black')};
`;

const TagName = styled.div`
  width: 25rem;
  font-size: 14px;
  color: var(--text-color-1);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Tag = styled.div<{added?: boolean}>`
  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
    background: #f2f7fa;
  }

  background: ${({added}) => (added ? '#f2f7fa' : '#fff')};

  > div {
    display: flex;
    align-items: center;
    width: 100%;
    height: 38px;
    padding: 0px 11px;

    ${TagName} {
      margin-left: 7px;
    }

    > button {
      margin-left: auto;
      padding: 0;
      svg {
        transform: rotate(90deg);
        width: 1rem;
        height: 1rem;
        stroke: var(--text-color-2);
        visibility: visible;
      }
    }
  }
`;

const HandleWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: 30px;
    height: 24px;
  }
`;

const DraggableItem = styled.div`
  width: 100%;
  border: none;
  min-height: 40px;
  display: flex;
  align-items: center;
  background-color: #fff;
  border-left: solid 3px #fff;
  cursor: pointer;
  &.dragging {
    background-color: #f3f9ff;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.13);
    border-left: solid 3px #68b4fb;
  }
`;
